"use client";
import { ChevronRight } from "lucide-react";
import React from "react";
import {
  Breadcrumb as AriaBreadcrumb,
  Breadcrumbs as AriaBreadcrumbs,
  type BreadcrumbProps,
  type BreadcrumbsProps,
} from "react-aria-components";

// Re-export types for consumers
export type { BreadcrumbProps, BreadcrumbsProps };
export type BreadcrumbItemProps = BreadcrumbProps & {
  href?: string;
  /**
   * When true, adds a copy-to-clipboard button next to the current (last) breadcrumb item.
   * The value copied is determined by `copyValue` prop, or falls back to the text content.
   */
  copyable?: boolean;
  /**
   * The value to copy when the copy button is clicked.
   * If not provided, the text content of the breadcrumb will be used.
   */
  copyValue?: string;
};

import { twMerge } from "tailwind-merge";
import { useElementSize } from "../../hooks/useElementSize";
import { composeTailwindRenderProps } from "../../utils";
import { type ActionItem, ActionMenu } from "../ActionMenu";
import { Button } from "../Button";
import { CopyToClipboard } from "../CopyToClipboard";
import { TextLink } from "../TextLink";

type BreadcrumbEllipsisProps = {
  items: Array<{ label: string; href?: string }>;
};

/**
 * Breadcrumbs
 *
 * Navigation breadcrumbs that show the user's current location in the hierarchy
 * Automatically collapses middle items into an ellipsis dropdown when the container
 * width exceeds the maxWidth threshold (default: 600px)
 */
export function Breadcrumbs<T extends object>(props: BreadcrumbsProps<T> & { maxWidth?: number }) {
  const { children, maxWidth = 600, className, ...restProps } = props;
  const { ref, width } = useElementSize();
  const [shouldCollapse, setShouldCollapse] = React.useState(false);
  const [isReady, setIsReady] = React.useState(false);
  const hasCheckedWidth = React.useRef(false);

  // Handle static children with automatic width-based collapsing
  const childrenArray = React.useMemo(() => {
    if (children && !props.items && typeof children !== "function") {
      return React.Children.toArray(children);
    }
    return null;
  }, [children, props.items]);

  // Determine if we should collapse based on width (only check once after initial render)
  React.useEffect(() => {
    if (childrenArray && childrenArray.length >= 4 && width > 0 && !hasCheckedWidth.current) {
      hasCheckedWidth.current = true;
      setShouldCollapse(width > maxWidth);
      setIsReady(true);
    } else if (!childrenArray || (childrenArray && childrenArray.length < 4)) {
      // If no collapsing needed, show immediately
      setIsReady(true);
    }

    // Fallback: if width hasn't been measured after 100ms, show anyway to prevent invisible breadcrumbs
    const fallbackTimeout = setTimeout(() => {
      if (!isReady) {
        setIsReady(true);
      }
    }, 100);

    return () => clearTimeout(fallbackTimeout);
  }, [width, maxWidth, childrenArray, isReady]);

  // Render collapsed version
  if (childrenArray && shouldCollapse && childrenArray.length >= 4) {
    const firstChild = childrenArray[0];
    const middleChildren = childrenArray.slice(1, -2);
    const lastTwoChildren = childrenArray.slice(-2);

    // Extract labels and hrefs from middle children for the ellipsis dropdown
    const middleLabels = middleChildren.map((child) => {
      if (React.isValidElement(child)) {
        const childProps = child.props as {
          children?: React.ReactNode;
          href?: string;
        };
        if (typeof childProps.children === "string") {
          return { label: childProps.children, href: childProps.href };
        }
      }
      return { label: "..." };
    });

    return (
      <AriaBreadcrumbs
        {...restProps}
        ref={ref}
        className={twMerge("flex gap-2", isReady ? "opacity-100" : "opacity-0", className)}
      >
        {firstChild as React.ReactNode}
        <BreadcrumbEllipsis items={middleLabels} />
        {lastTwoChildren as React.ReactNode[]}
      </AriaBreadcrumbs>
    );
  }

  // Default rendering (non-collapsed or dynamic items mode)
  return (
    <AriaBreadcrumbs
      {...restProps}
      ref={ref}
      className={twMerge("flex gap-2", isReady ? "opacity-100" : "opacity-0", className)}
    >
      {children as React.ReactNode}
    </AriaBreadcrumbs>
  );
}

/**
 * BreadcrumbEllipsis
 *
 * Internal component that renders collapsed breadcrumb items in an ActionMenu dropdown
 */
function BreadcrumbEllipsis({ items }: BreadcrumbEllipsisProps) {
  const actionItems: ActionItem[] = items.map((item, index) => ({
    id: `breadcrumb-${index}`,
    name: item.label,
    href: item.href,
  }));

  return (
    <AriaBreadcrumb className="flex items-center gap-2">
      <span className="flex items-center gap-2">
        <ActionMenu items={actionItems} align="left" size="sm">
          <Button variant="icon" size="sm" icon="DotsThree" aria-label="Show hidden breadcrumbs" className="px-0.5" />
        </ActionMenu>
        <ChevronRight className="h-4 w-4 text-text-secondary" />
      </span>
    </AriaBreadcrumb>
  );
}

/**
 * BreadcrumbItem
 *
 * Individual breadcrumb item with optional link
 * The last breadcrumb is automatically styled as the current page (non-interactive, bold)
 * When `copyable` is true, adds a copy-to-clipboard button next to the current (last) breadcrumb
 */
export function BreadcrumbItem(props: BreadcrumbItemProps) {
  const { className, children, href, copyable, copyValue, ...restProps } = props;
  return (
    <AriaBreadcrumb className={composeTailwindRenderProps(className, "flex items-center gap-2")} {...restProps}>
      {(renderProps) => {
        const isCurrent = renderProps.isCurrent;
        const renderedChildren = typeof children === "function" ? children(renderProps) : children;

        return (
          <>
            {isCurrent ? (
              copyable ? (
                <CopyToClipboard
                  value={copyValue || (typeof renderedChildren === "string" ? renderedChildren : undefined)}
                  size="sm"
                  iconPosition="right"
                >
                  <span className="max-w-[200px] truncate font-semibold text-text-primary sm:max-w-none">
                    {renderedChildren}
                  </span>
                </CopyToClipboard>
              ) : (
                <span className="max-w-[200px] truncate font-semibold text-text-primary sm:max-w-none">
                  {renderedChildren}
                </span>
              )
            ) : (
              <>
                <TextLink variant="muted" href={href} className="max-w-[150px] truncate sm:max-w-[300px] md:max-w-none">
                  {renderedChildren}
                </TextLink>
                <ChevronRight className="h-4 w-4 shrink-0 text-text-secondary" />
              </>
            )}
          </>
        );
      }}
    </AriaBreadcrumb>
  );
}
